// The pure half of the provider and model pickers (the `provider-picker` and `model-picker`
// widgets of the settings schema): what each option is, and how a tick changes the list.
// "*" means every provider; ticking it clears the specific ones and ticking a specific one
// clears it, so the list never says both.

export const ALL_PROVIDERS = "*";
/** Shown even before an installation reported an inventory. */
export const COMMON_PROVIDERS = ["anthropic", "openai", "amazon", "meta"] as const;

export interface ProviderOption {
  id: string;
  label: string;
}

export interface ModelOption {
  /** The family name the settings document holds. */
  name: string;
  provider: string;
  /** False for a name the document holds that the inventory does not list. */
  known: boolean;
}

const PROVIDER_LABELS: Record<string, string> = { anthropic: "Anthropic", openai: "OpenAI", amazon: "Amazon", meta: "Meta", mistral: "Mistral", cohere: "Cohere", deepseek: "DeepSeek" };

export function providerLabel(id: string): string {
  if (id === ALL_PROVIDERS) return "Every provider";
  return PROVIDER_LABELS[id] ?? id;
}

/** Anthropic and OpenAI first, then the rest alphabetically. */
export function compareProviders(a: string, b: string): number {
  const rank = (id: string) => (id === "anthropic" ? 0 : id === "openai" ? 1 : 2);
  return rank(a) - rank(b) || a.localeCompare(b);
}

/** The providers to offer: the common ones, the inventory's and whatever the value already names. */
export function providerOptions(inventoryProviders: readonly string[], current: readonly string[]): ProviderOption[] {
  const ids = new Set<string>([...COMMON_PROVIDERS, ...inventoryProviders, ...current.filter((p) => p !== ALL_PROVIDERS)]);
  return [ALL_PROVIDERS, ...[...ids].sort(compareProviders)].map((id) => ({ id, label: providerLabel(id) }));
}

export function toggleProvider(value: readonly string[], provider: string, on: boolean): string[] {
  if (provider === ALL_PROVIDERS) return on ? [ALL_PROVIDERS] : value.filter((p) => p !== ALL_PROVIDERS);
  const rest = value.filter((p) => p !== provider && p !== ALL_PROVIDERS);
  return on ? [...rest, provider] : rest;
}

export function toggleModel(value: readonly string[], name: string, on: boolean): string[] {
  const rest = value.filter((n) => n !== name);
  return on ? [...rest, name] : rest;
}

/** The inventory's models (a family name once each) plus any name the value holds that it lacks. */
export function modelOptions(groups: readonly { provider: string; names: readonly string[] }[], current: readonly string[]): ModelOption[] {
  const seen = new Set<string>();
  const out: ModelOption[] = [];
  for (const group of [...groups].sort((a, b) => compareProviders(a.provider, b.provider))) {
    for (const name of [...group.names].sort()) {
      if (seen.has(name)) continue;
      seen.add(name);
      out.push({ name, provider: group.provider, known: true });
    }
  }
  for (const name of current) {
    if (name !== "" && !seen.has(name)) {
      seen.add(name);
      out.push({ name, provider: "", known: false });
    }
  }
  return out;
}

/** Narrow the options by what the admin typed (case-insensitive, name or provider). */
export function filterModels(options: readonly ModelOption[], query: string): ModelOption[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [...options];
  return options.filter((o) => o.name.toLowerCase().includes(q) || o.provider.toLowerCase().includes(q));
}
